import React from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  Alert,
  Chip,
  Stack,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Button } from '@mui/material';

const Disclaimer = () => {
  const navigate = useNavigate();

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ textTransform: 'none' }}
        >
          Back
        </Button>
        <Typography variant="h4" fontWeight={700} sx={{ flex: 1 }}>
          Loksy – Platform Disclaimer
        </Typography>
      </Box>

      <Paper elevation={2} sx={{ p: 4, borderRadius: 3 }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography fontWeight={700} gutterBottom>
            Plain language
          </Typography>
          Locsy is a technology platform / online intermediary. Ads, shop info,
          job alerts, coupons, pamphlets, and posts are published by Mitras or
          Citizens — not as Locsy official government or employer notices. Locsy
          is not liable for third-party content. Always verify before you act.
        </Alert>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
          <Chip label="IT Act, 2000 · §79" size="small" />
          <Chip label="Intermediary Guidelines" size="small" />
          <Chip label="UGC not Locsy-authored" size="small" />
          <Chip label="Updated 22 Jul 2026" size="small" variant="outlined" />
        </Stack>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" fontWeight={700} gutterBottom color="primary">
            A. Intermediary status & liability (Locsy Citizen + Mitra)
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText
                primary="Loksy operates as an online intermediary and technology marketplace under the Information Technology Act, 2000 and the Intermediary Guidelines (as amended)."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Locsy does not create, author, or officially certify most listings, ads, job alerts, coupons, reviews, pamphlets, or citizen posts shown in the apps."
                secondary="(మూడవ పక్ష / user content — Locsy official publisher కాదు)"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Subject to applicable law (including Section 79 safe-harbour conditions and due diligence), Loksy, its affiliates, and officers are not liable for content published by Mitras, Citizens, or other third parties."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="A Locsy listing or verification badge is not a government licence, quality certification, or endorsement."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Users must independently verify critical details on official websites, offices, or documents before applying, paying, visiting, or relying on any information."
                secondary="(చర్య తీసుకునే ముందు official sources verify చేసుకోండి)"
              />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" fontWeight={700} gutterBottom color="primary">
            B. Prohibited / unlawful content
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary="Illegal goods/services, controlled substances, weapons, or categories prohibited under Indian law." />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Fake, misleading, or unofficial job / exam / government notifications presented as official."
                secondary="(నకిలీ / unofficial notices ని official లా చూపడం నిషేధం)"
              />
            </ListItem>
            <ListItem>
              <ListItemText primary="Fraudulent coupons, phishing, impersonation, hate, obscenity, defamation, or IP infringement." />
            </ListItem>
            <ListItem>
              <ListItemText primary="Publishers remain solely responsible for their content and may face account action and penalties under applicable law (including the IT Act and Bharatiya Nyaya Sanhita)." />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" fontWeight={700} gutterBottom color="primary">
            C. Moderation, takedown & grievance
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary="Locsy may review, edit visibility, unlist, suspend, or remove content or accounts for policy, safety, legal requests, or court / government orders." />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Report unlawful or harmful content to support@knls.in with post/shop details."
                secondary="Grievance / support: support@knls.in"
              />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" fontWeight={700} gutterBottom color="primary">
            1. Coupons (Offers / Discounts)
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText
                primary="Applying a coupon on a bill depends entirely on the Mitra’s willingness. The Mitra has full rights to retrieve & apply, edit the amount, or refuse the coupon at billing time — without prior notice."
                secondary="(bill lo coupon apply avvadam Mitra willingness meede; Mitra agree / disagree, edit, or skip cheyachu — Locsy force cheyadu)"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Admin / Mitra can pause templates, remove them from Locsy DigiDrop (“Share with Locsy”), or delete issued coupons at any time without notification."
                secondary="(pause / Share with Locsy off / delete — Mitra or admin eppudaina cheyochu)"
              />
            </ListItem>
            <ListItem>
              <ListItemText primary="Coupons unlocked via DigiDrop or allotted to a citizen are not a guaranteed redemption. Validity, stock, shop policy, and Mitra honouring apply. Locsy is not liable if a Mitra does not honour, edits, or refuses an offer." />
            </ListItem>
            <ListItem>
              <ListItemText primary="Locsy is only a technology platform connecting citizens and Mitras. Disputes about coupon value, services, or refusal are between the citizen and the Mitra." />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" fontWeight={700} gutterBottom color="primary">
            2. Job Alerts
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText
                primary="Job alerts displayed in Loksy are for information purpose only."
                secondary="(vi just information kosam matrame)"
              />
            </ListItem>
            <ListItem>
              <ListItemText primary="These alerts are NOT authenticated or officially verified government / employer notifications unless expressly labelled otherwise." />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Users must check official government / department / organization websites for accurate details."
                secondary="(accurate information kosam official sites lo verify chesukovali)"
              />
            </ListItem>
            <ListItem>
              <ListItemText primary="Loksy is not responsible for any errors, changes, or misunderstandings regarding job notifications." />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" fontWeight={700} gutterBottom color="primary">
            3. Services
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These services include: Aadhaar updates, PAN services, Passport services, Voter ID services, RTA services (LLR, Driving Licence), tickets, applications, color prints, online forms, caste/birth certificates, scholarship applications, works related assistance, exam registrations, corrections, PVC cards, DTP services, printing services (Xerox, WhatsApp prints), photo services, PF services, Sramik Card, Ayushman Bharat, EHS Cards, Udyam Aadhar, Hall Ticket prints, online status checks, and other miscellaneous services.
          </Typography>
          <Typography variant="h6" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
            Disclaimer:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary="KNLS provides assistance only." />
            </ListItem>
            <ListItem>
              <ListItemText primary="Final approval, processing speed, status update or rejection is completely under the respective government department / portal control, not KNLS." />
            </ListItem>
            <ListItem>
              <ListItemText primary="Users must provide correct details & documents. Wrong inputs result in delays or rejections, for which Loksy is not responsible." />
            </ListItem>
            <ListItem>
              <ListItemText primary="Once the application is submitted online, corrections may not be possible." />
            </ListItem>
            <ListItem>
              <ListItemText primary="Government site server issues, technical downtime, portal errors → KNLS not responsible." />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" fontWeight={700} gutterBottom color="primary">
            4. Courses (LMS / Training Programs)
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText
                primary="No refunds, no transfers, no cancellations for any course after enrollment."
                secondary="(refunds or course transfer ki possibility ledu)"
              />
            </ListItem>
            <ListItem>
              <ListItemText primary="Course content, duration, modules may be updated or changed anytime without prior notice." />
            </ListItem>
            <ListItem>
              <ListItemText primary="Loksy is not responsible for technical issues arising from user devices, internet problems, or login issues not related to the platform." />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" fontWeight={700} gutterBottom color="primary">
            5. Payments / Billing
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary="Once payment is made for coupons, services, or courses, it is treated as final." />
            </ListItem>
            <ListItem>
              <ListItemText primary="Any payment disputes must be raised with proper proof." />
            </ListItem>
            <ListItem>
              <ListItemText primary="Loksy is not responsible for UPI delays, bank-side failures, or pending transactions." />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" fontWeight={700} gutterBottom color="primary">
            6. User Responsibility
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary="All details entered by the user must be true and accurate." />
            </ListItem>
            <ListItem>
              <ListItemText primary="Uploading wrong data, wrong documents, or incomplete information is the user's responsibility." />
            </ListItem>
            <ListItem>
              <ListItemText primary="Loksy is not liable for consequences arising from incorrect information or third-party published content." />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" fontWeight={700} gutterBottom color="primary">
            7. General Terms
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary="Loksy reserves the right to modify, suspend, or remove any service or feature at any time." />
            </ListItem>
            <ListItem>
              <ListItemText primary="Loksy is not responsible for government policy updates, changes in rules, or processing delays." />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="By using the Loksy platform, users agree to all the above terms, the Terms of Service, and Privacy Policy."
                sx={{ fontWeight: 600 }}
              />
            </ListItem>
          </List>
        </Box>

        <Box
          sx={{
            mt: 4,
            p: 3,
            bgcolor: 'background.default',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="body2" color="text.secondary" align="center">
            Last Updated: 12 September 2026
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
            For queries or content reports: support@knls.in · Full pages:
            /privacy-policy · /locsy-terms-and-conditions · /disclaimer
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default Disclaimer;
